/**
 * Prices of a train, by class. Italo sells 4 environments × 3 fares, so `class_prices` has one row
 * per (class, fare): here they become one row per class, quoted at its cheapest bookable fare,
 * with the fares to choose from when there is more than one. (Same rules as the old kiosk,
 * backend/frontend/src/types/v3.ts groupPricesByClass and its tests.)
 */
import type { ClassPrice, Train } from './session';

export interface FareTier {
  fare: string;
  price: number;
  available: boolean;
}

export interface ClassRow {
  name: string;
  /** Cheapest bookable price, or the cheapest of all if none is bookable. */
  price: number;
  available: boolean;
  /** More than one bookable fare: show "from". */
  fromPrice: boolean;
  /** The fares, cheapest first, only when there is more than one. */
  tiers: FareTier[];
}

const bookable = (r: ClassPrice) => r.seats > 0 && r.price > 0;

export function classRows(prices: readonly ClassPrice[]): ClassRow[] {
  const byName = new Map<string, ClassPrice[]>();
  for (const p of prices) byName.set(p.name, [...(byName.get(p.name) ?? []), p]);
  return [...byName.entries()].map(([name, rows]) => {
    const ok = rows.filter(bookable);
    const pool = ok.length > 0 ? ok : rows;
    const tiers = rows
      .filter((r) => r.fare)
      .map((r) => ({ fare: String(r.fare), price: r.price, available: bookable(r) }))
      .sort((a, b) => a.price - b.price);
    return {
      name,
      price: Math.min(...pool.map((r) => r.price)),
      available: ok.length > 0,
      fromPrice: ok.length > 1,
      tiers: tiers.length > 1 ? tiers : [],
    };
  });
}

const FLEETS: Record<string, string> = { italo_evo: 'EVO', italo_agv575: 'AGV 575' };

/** "Italo" → "Alta Velocità · EVO"; a type we do not know keeps its own name. */
export function trainTypeLabel(train: Pick<Train, 'train_type' | 'rolling_stock'>, highSpeedLabel: string): string {
  const type = train.train_type.toLowerCase();
  const base = /italo|frecciarossa|^fr$/.test(type) ? highSpeedLabel : train.train_type;
  const fleet = train.rolling_stock ? FLEETS[train.rolling_stock] : undefined;
  return fleet ? `${base} · ${fleet}` : base;
}

export function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}
